import { CapacityOutcome, DeprivationOutcome, DolsTrackingStatus, } from "../types/capacityState/CapacityState.js";
/**
 * Demo data for MCA/DoLS resident oversight.
 *
 * The provider group is deliberately NOT a data controller. Each region is its own
 * controller, which is the arrangement a large care provider actually has — and the
 * reason nothing in the register may assume one controller per provider.
 */
export const elysiumGroup = {
    id: "5f9b1c40-0000-4000-8000-000000000001",
    name: "Elysium Healthcare (Thalamos Dev)",
    address: "1 Ridgeway Court, Grovewood Road",
    postalCode: "SL6 1AA",
    role: "independent",
    codes: [],
    // A group that administers the users of the regions beneath it, but controls no
    // patient data of its own.
    dataControllerId: "5f9b1c40-0000-4000-8000-000000000001",
    managingOrganisationId: null,
    isUserManagementOrganisation: true,
    status: "active",
};
export const elysiumSouthRegion = {
    id: "5f9b1c40-0000-4000-8000-000000000002",
    name: "Elysium South Region",
    address: "12 Bath Road, Reading",
    postalCode: "RG1 6NN",
    role: "independent",
    codes: [],
    dataControllerId: "5f9b1c40-0000-4000-8000-000000000002",
    managingOrganisationId: elysiumGroup.id,
    status: "active",
};
/**
 * A second controller under the same group. Exists so a resident moving between
 * regions exercises the cross-controller path rather than looking like a field edit.
 */
export const elysiumNorthRegion = {
    id: "5f9b1c40-0000-4000-8000-000000000003",
    name: "Elysium North Region",
    address: "40 Wellington Street, Leeds",
    postalCode: "LS1 2DE",
    role: "independent",
    codes: [],
    dataControllerId: "5f9b1c40-0000-4000-8000-000000000003",
    managingOrganisationId: elysiumGroup.id,
    status: "active",
};
const mcaConfiguration = {
    mcaEnabled: true,
    mcaOversightEnabled: true,
    nhsNumberEnabled: true,
    patientStateEnabled: true,
    // Adding a resident reuses patient search, which reaches the Personal Demographics
    // Service: without this a home could only add somebody eMHA already knew about.
    pdsEnabled: true,
    pdsEndUserOrganisationODSCode: "8HN02",
};
/**
 * The two regions are separate data controllers with their own governance, and they differ
 * deliberately: South sets a review interval, North sets none. A register with no
 * interval makes no claim about whether an assessment is still current, which is the
 * honest default and worth being able to see.
 */
export const elysiumSouthRegionConfiguration = {
    id: elysiumSouthRegion.id,
    configuration: { ...mcaConfiguration, mcaCapacityReviewIntervalMonths: 12 },
};
export const elysiumNorthRegionConfiguration = { id: elysiumNorthRegion.id, configuration: mcaConfiguration };
export const elysiumSouthMcaTeam = {
    id: "5f9b1c40-0000-4000-8000-000000000010",
    organisationId: elysiumSouthRegion.id,
    name: "Elysium South Region MCA Governance",
    type: "mca",
    primaryEmail: "aspire-dev-elysium-south-mca@aspire.thalamos.co.uk",
    status: "active",
};
export const elysiumNorthMcaTeam = {
    id: "5f9b1c40-0000-4000-8000-000000000011",
    organisationId: elysiumNorthRegion.id,
    name: "Elysium North Region MCA Governance",
    type: "mca",
    primaryEmail: "aspire-dev-elysium-north-mca@aspire.thalamos.co.uk",
    status: "active",
};
/**
 * Care homes, as curated managed locations. Fixed IDs so grants, residencies and
 * the register all reference the same rows across reseeds.
 *
 * `supervisoryLocalAuthorityName` is resolved to an ID at seed time — the local
 * authorities are themselves managed location rows, seeded from ODS data.
 */
export const elysiumCareHomes = [
    {
        id: "5f9b1c40-0000-4000-8000-000000000101",
        name: "Heathfield Manor",
        address: "Heathfield Lane, Newbury",
        postalCode: "RG14 2PL",
        cqcLocationId: "1-1000000001",
        supervisoryLocalAuthorityName: "West Berkshire Council",
        region: "south",
    },
    {
        id: "5f9b1c40-0000-4000-8000-000000000102",
        name: "Riverside Lodge",
        address: "2 Riverside Way, Reading",
        postalCode: "RG1 8DQ",
        cqcLocationId: "1-1000000002",
        supervisoryLocalAuthorityName: "Reading Borough Council",
        region: "south",
    },
    {
        id: "5f9b1c40-0000-4000-8000-000000000103",
        name: "Oakwood Care Home",
        address: "Oakwood Rise, Basingstoke",
        postalCode: "RG21 4TT",
        cqcLocationId: "1-1000000003",
        supervisoryLocalAuthorityName: "Basingstoke And Deane Borough Council",
        region: "south",
    },
    {
        id: "5f9b1c40-0000-4000-8000-000000000104",
        name: "Meadow View",
        address: "Meadow View Drive, Guildford",
        postalCode: "GU1 3RS",
        cqcLocationId: "1-1000000004",
        supervisoryLocalAuthorityName: "Guildford Borough Council",
        region: "south",
    },
    {
        id: "5f9b1c40-0000-4000-8000-000000000105",
        name: "Bramble Hill",
        address: "Bramble Hill Road, Harrogate",
        postalCode: "HG1 4RT",
        cqcLocationId: "1-1000000005",
        supervisoryLocalAuthorityName: "Harrogate Borough Council",
        region: "north",
    },
];
/**
 * A manager curates the register; a member assesses and records. That is the split
 * the existing team roles already carry, so no new role value is needed.
 */
export const elysiumSouthMcaLeadUser = {
    id: "5f9b1c40-0000-4000-8000-000000000020",
    name: "Joyce Okafor",
    email: "joyce.okafor@aspire.thalamos.co.uk",
    address: { address: "12 Bath Road, Reading", postalCode: "RG1 6NN" },
    accreditations: [{ type: "social-worker", registrationNumber: "SW104721" }],
    memberships: [
        { type: "team", teamId: elysiumSouthMcaTeam.id, role: "member" },
    ],
    authenticationMethods: [],
    isOnboarded: true,
    hasPidAccess: true,
    hasPatientMergeAccess: false,
    status: "active",
};
export const elysiumSouthMcaManagerUser = {
    id: "5f9b1c40-0000-4000-8000-000000000021",
    name: "Priya Raman",
    email: "priya.raman@aspire.thalamos.co.uk",
    address: { address: "12 Bath Road, Reading", postalCode: "RG1 6NN" },
    accreditations: [],
    memberships: [
        { type: "team", teamId: elysiumSouthMcaTeam.id, role: "manager" },
    ],
    authenticationMethods: [],
    isOnboarded: true,
    hasPidAccess: true,
    hasPatientMergeAccess: false,
    status: "active",
};
/**
 * A doctor who signs the capacity assessments. Sits in the region rather than the
 * MCA team so the signing requirement on the MCA templates is exercised.
 */
export const elysiumSouthAssessorUser = {
    id: "5f9b1c40-0000-4000-8000-000000000022",
    name: "Dr Anil Patel",
    email: "anil.patel@aspire.thalamos.co.uk",
    address: { address: "12 Bath Road, Reading", postalCode: "RG1 6NN" },
    accreditations: [{ type: "doctor", gmcNumber: "7412589" }],
    memberships: [
        { type: "team", teamId: elysiumSouthMcaTeam.id, role: "member" },
    ],
    authenticationMethods: [],
    isOnboarded: true,
    hasPidAccess: true,
    hasPatientMergeAccess: false,
    status: "active",
};
export const elysiumNorthMcaLeadUser = {
    id: "5f9b1c40-0000-4000-8000-000000000023",
    name: "Tom Whitfield",
    email: "tom.whitfield@aspire.thalamos.co.uk",
    address: { address: "40 Wellington Street, Leeds", postalCode: "LS1 2DE" },
    accreditations: [{ type: "nurse", pinNumber: "84B1220C" }],
    memberships: [
        { type: "team", teamId: elysiumNorthMcaTeam.id, role: "member" },
    ],
    authenticationMethods: [],
    isOnboarded: true,
    hasPidAccess: true,
    hasPatientMergeAccess: false,
    status: "active",
};
export const elysiumOrganisations = [
    elysiumGroup,
    elysiumSouthRegion,
    elysiumNorthRegion,
];
export const elysiumOrganisationConfigurations = [
    elysiumSouthRegionConfiguration,
    elysiumNorthRegionConfiguration,
];
export const elysiumTeams = [elysiumSouthMcaTeam, elysiumNorthMcaTeam];
export const elysiumUsers = [
    elysiumSouthMcaLeadUser,
    elysiumSouthMcaManagerUser,
    elysiumSouthAssessorUser,
    elysiumNorthMcaLeadUser,
];
const heathfield = elysiumCareHomes[0].id;
const riverside = elysiumCareHomes[1].id;
const oakwood = elysiumCareHomes[2].id;
const meadowView = elysiumCareHomes[3].id;
const brambleHill = elysiumCareHomes[4].id;
export const elysiumResidents = [
    {
        patientId: "5f9b1c40-0000-4000-8000-000000000201",
        name: { given: "Patricia", family: "Ashworth" },
        dateOfBirth: "1944-03-19",
        address: { address: "Heathfield Lane, Newbury", postalCode: "RG14 2PL" },
        careHomeId: heathfield,
        residencyStartDateTime: "2024-02-10T09:00:00.000Z",
        capacityOutcome: CapacityOutcome.HasCapacity,
        deprivationOutcome: DeprivationOutcome.NotDeprived,
        assessedMonthsAgo: 2,
    },
    {
        // Capacity assessed, the deprivation question still open: the cell where the register
        // is waiting on a consent finding before it can say anything about DoLS.
        patientId: "5f9b1c40-0000-4000-8000-00000000020e",
        name: { given: "Vera", family: "Mottram" },
        dateOfBirth: "1938-11-02",
        address: { address: "8 Heathfield Lane, Newbury", postalCode: "RG14 2PL" },
        careHomeId: heathfield,
        residencyStartDateTime: "2025-11-14T09:00:00.000Z",
        capacityOutcome: CapacityOutcome.HasCapacity,
        deprivationOutcome: DeprivationOutcome.NotAssessed,
        assessedMonthsAgo: 1,
    },
    {
        patientId: "5f9b1c40-0000-4000-8000-00000000020f",
        name: { given: "Leonard", family: "Vasey" },
        dateOfBirth: "1936-06-21",
        address: { address: "3 Riverside Way, Reading", postalCode: "RG1 8DQ" },
        careHomeId: riverside,
        residencyStartDateTime: "2025-12-01T09:00:00.000Z",
        capacityOutcome: CapacityOutcome.LacksCapacity,
        deprivationOutcome: DeprivationOutcome.NotAssessed,
        assessedMonthsAgo: 1,
    },
    {
        patientId: "5f9b1c40-0000-4000-8000-000000000202",
        name: { given: "Edith", family: "Barrow" },
        dateOfBirth: "1940-08-19",
        address: { address: "2 Riverside Way, Reading", postalCode: "RG1 8DQ" },
        careHomeId: riverside,
        residencyStartDateTime: "2023-05-02T09:00:00.000Z",
        capacityOutcome: CapacityOutcome.LacksCapacity,
        deprivationOutcome: DeprivationOutcome.Deprived,
        dols: {
            status: DolsTrackingStatus.StandardAuthorisationGranted,
            expiryInDays: 6,
        },
    },
    {
        patientId: "5f9b1c40-0000-4000-8000-000000000203",
        name: { given: "Robert", family: "Blackwell" },
        dateOfBirth: "1937-07-25",
        address: { address: "Heathfield Lane, Newbury", postalCode: "RG14 2PL" },
        careHomeId: heathfield,
        residencyStartDateTime: "2026-07-20T09:00:00.000Z",
        capacityOutcome: CapacityOutcome.LacksCapacity,
        deprivationOutcome: DeprivationOutcome.Deprived,
        // The case a single status column cannot hold: a self-authorised hold running
        // while the standard application is still with the local authority.
        dols: { status: DolsTrackingStatus.ReferralInProgress },
        urgentAuthorisationExpiryInDays: 3,
    },
    {
        patientId: "5f9b1c40-0000-4000-8000-000000000204",
        name: { given: "William", family: "Clarke" },
        dateOfBirth: "1939-09-06",
        address: { address: "Oakwood Rise, Basingstoke", postalCode: "RG21 4TT" },
        careHomeId: oakwood,
        residencyStartDateTime: "2022-11-14T09:00:00.000Z",
        capacityOutcome: CapacityOutcome.LacksCapacity,
        deprivationOutcome: DeprivationOutcome.Deprived,
        dols: {
            status: DolsTrackingStatus.StandardAuthorisationGranted,
            expiryInDays: 57,
        },
    },
    {
        patientId: "5f9b1c40-0000-4000-8000-000000000205",
        name: { given: "Norman", family: "Davies" },
        dateOfBirth: "1941-09-11",
        address: { address: "Heathfield Lane, Newbury", postalCode: "RG14 2PL" },
        careHomeId: heathfield,
        residencyStartDateTime: "2021-03-01T09:00:00.000Z",
        capacityOutcome: CapacityOutcome.LacksCapacity,
        deprivationOutcome: DeprivationOutcome.Deprived,
        dols: {
            status: DolsTrackingStatus.StandardAuthorisationExpired,
            expiryInDays: -15,
        },
    },
    {
        patientId: "5f9b1c40-0000-4000-8000-000000000206",
        name: { given: "Arthur", family: "Jenkins" },
        dateOfBirth: "1942-06-14",
        address: { address: "Oakwood Rise, Basingstoke", postalCode: "RG21 4TT" },
        careHomeId: oakwood,
        residencyStartDateTime: "2026-06-30T09:00:00.000Z",
        capacityOutcome: CapacityOutcome.LacksCapacity,
        deprivationOutcome: DeprivationOutcome.Deprived,
        // Nothing has been sent to the local authority. No clock in the prototype, and
        // the state most likely to sit unnoticed.
        dols: { status: DolsTrackingStatus.ReferralNotStarted },
    },
    {
        patientId: "5f9b1c40-0000-4000-8000-000000000207",
        name: { given: "Cyril", family: "Lawton" },
        dateOfBirth: "1938-04-03",
        address: { address: "2 Riverside Way, Reading", postalCode: "RG1 8DQ" },
        careHomeId: riverside,
        residencyStartDateTime: "2025-01-08T09:00:00.000Z",
        // Capacity present and liberty deprived: either he is consenting, in which case
        // it is not a deprivation, or he is unlawfully detained.
        capacityOutcome: CapacityOutcome.HasCapacity,
        deprivationOutcome: DeprivationOutcome.Deprived,
        assessedMonthsAgo: 3,
    },
    {
        patientId: "5f9b1c40-0000-4000-8000-000000000208",
        name: { given: "Florence", family: "Osei" },
        dateOfBirth: "1945-03-27",
        address: { address: "Oakwood Rise, Basingstoke", postalCode: "RG21 4TT" },
        careHomeId: oakwood,
        residencyStartDateTime: "2024-09-19T09:00:00.000Z",
        capacityOutcome: CapacityOutcome.LacksCapacity,
        deprivationOutcome: DeprivationOutcome.NotDeprived,
        assessedMonthsAgo: 14,
    },
    {
        patientId: "5f9b1c40-0000-4000-8000-000000000209",
        name: { given: "Doris", family: "Patel" },
        dateOfBirth: "1936-12-01",
        address: { address: "2 Riverside Way, Reading", postalCode: "RG1 8DQ" },
        careHomeId: riverside,
        residencyStartDateTime: "2023-08-30T09:00:00.000Z",
        capacityOutcome: CapacityOutcome.LacksCapacity,
        deprivationOutcome: DeprivationOutcome.Deprived,
        dols: {
            status: DolsTrackingStatus.StandardAuthorisationGranted,
            expiryInDays: 21,
        },
    },
    {
        patientId: "5f9b1c40-0000-4000-8000-000000000210",
        name: { given: "Beatrice", family: "Taylor" },
        dateOfBirth: "1943-08-16",
        address: { address: "Meadow View Drive, Guildford", postalCode: "GU1 3RS" },
        careHomeId: meadowView,
        residencyStartDateTime: "2025-06-11T09:00:00.000Z",
        capacityOutcome: CapacityOutcome.LacksCapacity,
        deprivationOutcome: DeprivationOutcome.NotDeprived,
        assessedMonthsAgo: 11,
    },
    {
        patientId: "5f9b1c40-0000-4000-8000-000000000211",
        name: { given: "Harold", family: "Jennings" },
        dateOfBirth: "1934-04-22",
        address: { address: "Meadow View Drive, Guildford", postalCode: "GU1 3RS" },
        careHomeId: meadowView,
        residencyStartDateTime: "2020-10-05T09:00:00.000Z",
        capacityOutcome: CapacityOutcome.LacksCapacity,
        deprivationOutcome: DeprivationOutcome.Deprived,
        dols: {
            status: DolsTrackingStatus.StandardAuthorisationGranted,
            expiryInDays: -2,
        },
    },
    {
        patientId: "5f9b1c40-0000-4000-8000-000000000212",
        name: { given: "Margaret", family: "Thompson" },
        dateOfBirth: "1938-11-03",
        address: { address: "Oakwood Rise, Basingstoke", postalCode: "RG21 4TT" },
        careHomeId: oakwood,
        residencyStartDateTime: "2026-07-22T09:00:00.000Z",
        // On the register, nothing assessed. Derives to No Status, which is the whole
        // point of having the cell.
        capacityOutcome: CapacityOutcome.NotAssessed,
        deprivationOutcome: DeprivationOutcome.NotAssessed,
    },
    {
        patientId: "5f9b1c40-0000-4000-8000-000000000213",
        name: { given: "Alan", family: "Fitzgerald" },
        dateOfBirth: "1947-02-28",
        address: { address: "Bramble Hill Road, Harrogate", postalCode: "HG1 4RT" },
        careHomeId: brambleHill,
        residencyStartDateTime: "2025-04-16T09:00:00.000Z",
        capacityOutcome: CapacityOutcome.LacksCapacity,
        deprivationOutcome: DeprivationOutcome.Deprived,
        dols: {
            status: DolsTrackingStatus.StandardAuthorisationGranted,
            expiryInDays: 120,
        },
    },
];
/** Which team holds a grant over which home, and at what level. */
export const elysiumCareHomeGrants = [
    ...elysiumCareHomes
        .filter((home) => home.region === "south")
        .map((home) => ({
        teamId: elysiumSouthMcaTeam.id,
        careHomeId: home.id,
        permission: "manage-register",
    })),
    ...elysiumCareHomes
        .filter((home) => home.region === "north")
        .map((home) => ({
        teamId: elysiumNorthMcaTeam.id,
        careHomeId: home.id,
        permission: "manage-register",
    })),
    // A read-only grant across a controller boundary: the North team can see one
    // South home but cannot change who is on it.
    {
        teamId: elysiumNorthMcaTeam.id,
        careHomeId: meadowView,
        permission: "view",
    },
];
//# sourceMappingURL=elysium.js.map